/**
 * 产品详情页面 — ProductDetail（按概念稿 product-detail.html 重排）。
 *
 * 信息架构（对齐概念稿，PRD §1.6 内容区块保留下移）：
 *   1. 产品头卡：品牌/类目/官网/生命周期（现有字段）+ GhsRing
 *   2. 指标卡 ×4：AI 可见性 / GHS / 收录率 / 事实包
 *   3. g83：左 7 日趋势（recharts，复用 AiVisibility 页 trend 取数模式）
 *           右 三层漏斗（FunnelView + index-ledger summary）
 *   4. g2：左 事实包摘要卡（最新版本 A/B 级计数 + 入口链接）
 *          右 关联预警卡（alerts 按 product 过滤，AlertItem 复用）
 *   5. PRD §1.6 ① 用户描述 / ② 客户案例+常见问题 / ③ 客户问题与产品匹配（原有，不删）
 *
 * 数据口径说明（不虚构接口，概念稿中接口拿不到的字段一律跳过）：
 *   - AI 可见性 = 产品详情聚合 ai_visibility 记录 mention_rate 均值
 *   - 收录率/漏斗 = GET /api/index-ledger/summary?product_id=（未就绪显 "—"/EmptyState）
 *   - 事实包 = GET /api/products/{id}/fact-packs 最新版本（未就绪显 "—"/EmptyState）
 *   - 趋势 = GET /api/ai-visibility/matrix 取首个话题×平台 → GET /api/ai-visibility/trend
 *   - 预警 = GET /api/alerts?brand_id= 客户端按 product_id 过滤（接口无 product_id 参数）
 *   - 各接口均为独立失败域，失败仅对应区块降级，不红屏
 *
 * 保留：批 1 EmptyState 兜底（无 id 引导）、批 5 事实包入口按钮（PageHeader actions）。
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Link,
  useTheme,
} from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  VisibilityOutlined,
  FactCheckOutlined,
  Inventory2Outlined,
} from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import StatCard from "../components/StatCard";
import Badge from "../components/Badge";
import GhsRing from "../components/GhsRing";
import FunnelView from "../components/FunnelView";
import AlertItem from "../components/AlertItem";
import { getProductDetail, getProblemMatches } from "../api/products";
import { getIndexLedgerSummary } from "../api/indexLedger";
import { getFactPacks } from "../api/factPacks";
import { getAlerts } from "../api/alerts";
import { getVisibilityMatrix, getVisibilityTrend } from "../api/visibility";
import { useCompanyStore } from "../store/companyStore";
import { formatDate, formatDateTime, ghsRating, ghsColor } from "../utils/format";
import type {
  ProductDetail as ProductDetailData,
  ProblemMatch,
  LedgerSummary,
  FactPack,
  AlertItem as AlertItemData,
  TrendPoint,
} from "../types";

/** 用户描述 4 组定义。 */
const DESC_GROUPS = [
  {
    title: "需求",
    items: [
      { key: "target_customer", label: "目标客户" },
      { key: "basic_needs", label: "基础需求点" },
    ],
  },
  {
    title: "功能",
    items: [
      { key: "product_info", label: "产品基础信息" },
      { key: "features", label: "功能参数" },
    ],
  },
  {
    title: "优点",
    items: [
      { key: "advantages", label: "优势" },
      { key: "patents", label: "专利/技术" },
    ],
  },
  {
    title: "价值",
    items: [{ key: "awards", label: "奖项" }],
  },
] as const;

export default function ProductDetail() {
  const muiTheme = useTheme();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);
  const effectiveProductId = productId || selectedProductId;
  const [data, setData] = useState<ProductDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 概念稿新增区块（均为独立失败域）
  const [ledger, setLedger] = useState<LedgerSummary | null>(null);
  const [ledgerFailed, setLedgerFailed] = useState(false);
  const [packs, setPacks] = useState<FactPack[] | null>(null);
  const [packsFailed, setPacksFailed] = useState(false);
  const [alerts, setAlerts] = useState<AlertItemData[] | null>(null);
  const [alertsFailed, setAlertsFailed] = useState(false);
  const [trendSel, setTrendSel] = useState<{ topic: string; platform: string } | null>(null);
  const [trendPoints, setTrendPoints] = useState<TrendPoint[]>([]);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendFailed, setTrendFailed] = useState(false);

  useEffect(() => {
    if (effectiveProductId) {
      setLoading(true);
      setError("");
      getProductDetail(effectiveProductId)
        .then(setData)
        .catch((err) =>
          setError(
            err instanceof Error ? err.message : "获取产品详情失败",
          ),
        )
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [effectiveProductId]);

  const [matchList, setMatchList] = useState<ProblemMatch[]>([]);

  useEffect(() => {
    if (effectiveProductId) {
      getProblemMatches(effectiveProductId)
        .then(setMatchList)
        .catch(() => setMatchList([]));
    } else {
      setMatchList([]);
    }
  }, [effectiveProductId]);

  const brandId = data?.brand?.id || data?.product.brand_id || "";

  /** 收录漏斗（指标卡"收录率" + 右侧漏斗卡共用；未就绪 → "—"/EmptyState）。 */
  useEffect(() => {
    if (!effectiveProductId) return;
    let cancelled = false;
    getIndexLedgerSummary(effectiveProductId)
      .then((s) => {
        if (!cancelled) {
          setLedger(s);
          setLedgerFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLedger(null);
          setLedgerFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveProductId]);

  /** 事实包版本列表（指标卡"事实包" + 摘要卡共用）。 */
  useEffect(() => {
    if (!effectiveProductId) return;
    let cancelled = false;
    getFactPacks(effectiveProductId)
      .then((res) => {
        if (!cancelled) {
          setPacks(res.items || []);
          setPacksFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPacks(null);
          setPacksFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveProductId]);

  /** 关联预警：alerts 接口无 product_id 参数，按品牌取后客户端过滤本产品。 */
  useEffect(() => {
    if (!effectiveProductId || !data) return;
    let cancelled = false;
    getAlerts({ brand_id: brandId || undefined, size: 50 })
      .then((res) => {
        if (cancelled) return;
        const open = (res.items || [])
          .filter(
            (a) => a.product_id === effectiveProductId && a.status !== "resolved",
          )
          .sort((a, b) => {
            if (a.level !== b.level) return a.level === "P0" ? -1 : 1;
            return (b.last_triggered_at || "").localeCompare(
              a.last_triggered_at || "",
            );
          });
        setAlerts(open);
        setAlertsFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setAlerts(null);
          setAlertsFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveProductId, data, brandId]);

  /**
   * 7 日趋势：复用 AiVisibility 页取数模式 ——
   * matrix 取首个话题 × 首个平台 → trend 取该组合 7 日点；
   * 无 topic 数据 / 端点未就绪 → EmptyState 降级。
   */
  useEffect(() => {
    if (!brandId) {
      if (data) {
        setTrendLoading(false);
        setTrendFailed(true);
      }
      return;
    }
    let cancelled = false;
    setTrendLoading(true);
    setTrendFailed(false);
    (async () => {
      try {
        const m = await getVisibilityMatrix(brandId, 7);
        const topic = m.topics?.[0];
        const platform = m.platforms?.[0];
        if (!topic || !platform) {
          // 矩阵就绪但无话题数据：非失败，空态展示
          if (!cancelled) {
            setTrendSel(null);
            setTrendPoints([]);
          }
          return;
        }
        const t = await getVisibilityTrend(topic, platform, brandId);
        if (!cancelled) {
          setTrendSel({ topic, platform });
          setTrendPoints(t.points || []);
        }
      } catch {
        if (!cancelled) {
          setTrendSel(null);
          setTrendPoints([]);
          setTrendFailed(true);
        }
      } finally {
        if (!cancelled) setTrendLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandId, data]);

  /** 标记处理成功：本地置 resolved（与 Dashboard 同模式）。 */
  const handleAlertResolved = (id: string) => {
    setAlerts((prev) =>
      prev
        ? prev.map((a) =>
            a.id === id ? { ...a, status: "resolved" as const } : a,
          )
        : prev,
    );
  };

  /** 趋势图数据（date 裁剪为 MM-DD）。 */
  const trendData = useMemo(
    () =>
      trendPoints.map((p) => ({
        date: p.date.length >= 10 ? p.date.slice(5, 10) : p.date,
        score: p.score,
      })),
    [trendPoints],
  );

  if (!effectiveProductId) {
    return (
      <Box>
        <PageHeader
          title="产品"
          breadcrumbs={[{ label: "资产管理" }, { label: "产品" }]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先选择一个产品"
              subtitle="使用页面顶部的产品选择器，选择要查看的产品"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <PageHeader title="产品详情" />
        <Loading fullPage text="正在加载产品详情..." />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <PageHeader title="产品详情" />
        <Card>
          <CardContent>
            <Typography sx={{ fontSize: 13, color: "error.main" }}>
              {error || "产品不存在"}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const { product } = data;
  const userDesc = (product.user_description && typeof product.user_description === "object" && !Array.isArray(product.user_description)) ? product.user_description : {};
  const cases = Array.isArray(product.customer_cases) ? product.customer_cases : [];
  const faqs = Array.isArray(product.faq_list) ? product.faq_list : [];

  /** AI 可见性均值（产品详情聚合 ai_visibility 记录口径）。 */
  const avgVisibility =
    data.ai_visibility && data.ai_visibility.length > 0
      ? data.ai_visibility.reduce((sum, i) => sum + i.mention_rate, 0) /
        data.ai_visibility.length
      : null;

  /** 最新事实包（列表按 finalized_at 倒序；优先定稿版）。 */
  const latestPack =
    packs && packs.length > 0
      ? packs.find((p) => p.status === "finalized") || packs[0]
      : null;

  const p0Count = alerts?.filter((a) => a.level === "P0").length ?? 0;
  const p1Count = alerts?.filter((a) => a.level === "P1").length ?? 0;

  return (
    <Box>
      <PageHeader
        title={product.name}
        subtitle={
          data.brand
            ? `${data.brand.name}${data.company ? `（${data.company.name}）` : ""} · 深链 /products/:id`
            : undefined
        }
        breadcrumbs={[
          { label: "资产管理" },
          { label: "产品", path: "/products" },
          { label: product.name },
        ]}
        actions={
          <Button
            variant="outlined"
            onClick={() => navigate(`/products/${effectiveProductId}/fact-packs`)}
          >
            事实包
          </Button>
        }
      />

      {/* ===== 产品头卡（概念稿：kv + GHS 环；接口无"上市时间/价格"字段，跳过不虚构） ===== */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                  品牌
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.primary" }}>
                  {data.brand
                    ? `${data.brand.name}${data.company ? `（${data.company.name}）` : ""}`
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                  类目
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: product.category ? "text.primary" : "text.disabled",
                  }}
                >
                  {product.category || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                  生命周期
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: product.lifecycle_stage ? "text.primary" : "text.disabled",
                  }}
                >
                  {product.lifecycle_stage || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                  官网链接
                </Typography>
                {product.url ? (
                  <Link
                    href={product.url}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ fontSize: 13, color: "primary.main", wordBreak: "break-all" }}
                  >
                    {product.url}
                  </Link>
                ) : (
                  <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                    —
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Box sx={{ textAlign: "center", flexShrink: 0 }}>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
                GHS 总分
              </Typography>
              <GhsRing score={product.ghs_score} size={80} thickness={6} showRating />
              <Box sx={{ mt: 0.5 }}>
                <Badge
                  label={ghsRating(product.ghs_score)}
                  color={ghsColor(product.ghs_score)}
                  bgColor="semanticBg.neutral"
                />
              </Box>
              <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.5 }}>
                <Link
                  component={RouterLink}
                  to={`/products/${effectiveProductId}/ghs`}
                  sx={{ fontSize: 11, color: "primary.main", textDecoration: "none" }}
                >
                  诊断报告 ›
                </Link>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ===== 指标卡 ×4（概念稿：AI 可见性 / GHS / 收录率 / 事实包） ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="AI 可见性"
            value={avgVisibility === null ? "—" : avgVisibility.toFixed(1)}
            subtitle={
              avgVisibility === null ? "洞悉引擎未产出" : "探测记录提及率均值"
            }
            icon={<VisibilityOutlined />}
            iconBg="brand.50"
            iconColor="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="GHS 总分"
            value={product.ghs_score}
            subtitle={`评级：${ghsRating(product.ghs_score)}`}
            icon={<FactCheckOutlined />}
            iconBg="semanticBg.success"
            iconColor="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="投放内容收录率"
            value={ledger ? (ledger.index_rate * 100).toFixed(1) : "—"}
            suffix={ledger ? "%" : undefined}
            subtitle={
              ledger
                ? `${ledger.indexed}/${ledger.produced} 篇`
                : "收录台账未接入"
            }
            icon={<Inventory2Outlined />}
            iconBg="semanticBg.info"
            iconColor="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="事实包"
            value={latestPack ? latestPack.version : "—"}
            subtitle={
              latestPack
                ? `${latestPack.item_count} 条${
                    latestPack.level_a_count !== undefined
                      ? ` · A 级 ${latestPack.level_a_count}${latestPack.level_b_count !== undefined ? ` / B 级 ${latestPack.level_b_count}` : ""}`
                      : ""
                  }`
                : packsFailed
                  ? "事实包接口未接入"
                  : "暂无事实包版本"
            }
            icon={<Inventory2Outlined />}
            iconBg="semanticBg.warning"
            iconColor="warning.main"
          />
        </Grid>
      </Grid>

      {/* ===== 7 日趋势 + 三层漏斗（概念稿 g83） ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h4">
                  AI 可见性 · 近 7 日趋势
                </Typography>
                {trendSel && <Badge label={trendSel.platform} variant="info" />}
                <Box sx={{ flexGrow: 1 }} />
                <Link
                  component={RouterLink}
                  to="/ai-visibility"
                  sx={{ fontSize: 12, color: "primary.main", textDecoration: "none" }}
                >
                  全景矩阵 ›
                </Link>
              </Box>
              {trendLoading ? (
                <Loading text="正在加载趋势..." />
              ) : trendFailed ? (
                <EmptyState
                  text="趋势数据暂未就绪"
                  source="由洞悉引擎周期探测产出，矩阵/趋势端点接入后自动展示"
                />
              ) : !trendSel || trendData.length === 0 ? (
                <EmptyState
                  text="暂无监测话题趋势数据"
                  source="由洞悉引擎周期探测产出 · 产出后自动展示"
                />
              ) : (
                <>
                  <Typography
                    sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}
                  >
                    话题「{trendSel.topic}」提及率得分
                  </Typography>
                  <Box sx={{ width: "100%", height: 240 }}>
                    <ResponsiveContainer>
                      <LineChart
                        data={trendData}
                        margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={muiTheme.palette.lineSoft}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="date"
                          tick={{
                            fontSize: 11,
                            fill: muiTheme.palette.text.disabled,
                          }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          domain={[0, 100]}
                          tick={{
                            fontSize: 11,
                            fill: muiTheme.palette.text.disabled,
                          }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <ChartTooltip
                          formatter={(value) => [`${value ?? "—"} 分`, "提及率得分"]}
                        />
                        <Line
                          type="monotone"
                          dataKey="score"
                          stroke={muiTheme.palette.primary.main}
                          strokeWidth={2}
                          dot={{ r: 2.5 }}
                          activeDot={{ r: 4 }}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>
                三层漏斗 · 本月
              </Typography>
              {ledger === null && !ledgerFailed ? (
                <Loading text="正在加载漏斗..." />
              ) : ledgerFailed || !ledger ? (
                <EmptyState
                  text="收录漏斗暂未接入"
                  source="由洞悉引擎收录管道产出，未接入时显示空态"
                />
              ) : (
                <FunnelView summary={ledger} drilldownLink="/index-ledger" />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== 事实包摘要 + 关联预警（概念稿 g2） ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h4">事实包摘要</Typography>
                {latestPack && <Badge label={`${latestPack.version} 当前`} variant="info" />}
                <Box sx={{ flexGrow: 1 }} />
                <Link
                  component={RouterLink}
                  to={`/products/${effectiveProductId}/fact-packs`}
                  sx={{ fontSize: 12, color: "primary.main", textDecoration: "none" }}
                >
                  查看全部 ›
                </Link>
              </Box>
              {packs === null && !packsFailed ? (
                <Loading text="正在加载事实包..." />
              ) : packsFailed ? (
                <EmptyState
                  text="事实包暂未接入"
                  source="由 geo-fact-pack skill 产出 · 端点未就绪时显示空态"
                />
              ) : !latestPack ? (
                <EmptyState
                  text="暂无事实包版本"
                  source="由 geo-fact-pack skill 产出 · 定稿入库后自动展示"
                />
              ) : (
                <Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", width: 72, flexShrink: 0 }}>
                        最新版本
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "text.primary" }}>
                        {latestPack.version} · 于 {formatDateTime(latestPack.finalized_at)} 入库
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Typography sx={{ fontSize: 12, color: "text.secondary", width: 72, flexShrink: 0 }}>
                        条目构成
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                        <Badge label={`共 ${latestPack.item_count} 条`} />
                        {latestPack.level_a_count !== undefined && (
                          <Badge label={`A 级 ${latestPack.level_a_count}`} variant="success" />
                        )}
                        {latestPack.level_b_count !== undefined && (
                          <Badge label={`B 级 ${latestPack.level_b_count}`} variant="info" />
                        )}
                      </Box>
                    </Box>
                    {latestPack.fresh_until && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Typography sx={{ fontSize: 12, color: "text.secondary", width: 72, flexShrink: 0 }}>
                          保鲜期
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "text.primary" }}>
                          至 {formatDate(latestPack.fresh_until)}
                        </Typography>
                      </Box>
                    )}
                    {latestPack.produced_by && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Typography sx={{ fontSize: 12, color: "text.secondary", width: 72, flexShrink: 0 }}>
                          产出方式
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "text.primary" }}>
                          {latestPack.produced_by}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "text.disabled",
                      mt: 2,
                      pt: 1.5,
                      borderTop: "1px solid",
                      borderColor: "lineSoft",
                    }}
                  >
                    「仅内部」条目对客户查看者不可见（接口侧过滤，PRD Q8 / FR2）。
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h4">当前预警 · 关联本产品</Typography>
                {alerts && p0Count > 0 && <Badge label={`P0 ×${p0Count}`} variant="error" />}
                {alerts && p1Count > 0 && <Badge label={`P1 ×${p1Count}`} variant="warning" />}
              </Box>
              {alerts === null && !alertsFailed ? (
                <Loading text="正在加载预警..." />
              ) : alertsFailed ? (
                <EmptyState
                  text="预警流暂未接入"
                  source="由预警引擎按规则周期巡检产出，联调完成后自动展示"
                />
              ) : alerts && alerts.length === 0 ? (
                <EmptyState
                  text="暂无本产品相关未处理预警"
                  source="由预警引擎按规则周期巡检产出"
                  hint="P0/P1 规则触发后自动出现在这里"
                />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {(alerts || []).slice(0, 5).map((a) => (
                    <AlertItem key={a.id} item={a} onResolved={handleAlertResolved} />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== ① 用户描述（PRD §1.6 保留区块：需求→功能→优点→价值） ===== */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography
            sx={{ fontSize: 14, fontWeight: 500, color: "primary.main", mb: 2 }}
          >
            ① 用户描述
          </Typography>
          <Grid container spacing={2}>
            {DESC_GROUPS.map((group) => (
              <Grid item xs={12} sm={6} md={3} key={group.title}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1 }}>
                  {group.title}
                </Typography>
                {group.items.map((item) => {
                  const value = (userDesc as Record<string, string>)[item.key];
                  return (
                    <Box key={item.key} sx={{ mb: 1 }}>
                      <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                        {item.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: value ? "text.primary" : "text.disabled",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {value || "—"}
                      </Typography>
                    </Box>
                  );
                })}
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* ===== ② 客户案例 + 常见问题（PRD §1.6 保留区块：两类并列·逐条卡片陈列） ===== */}
      <Grid container spacing={2} sx={{ mb: 0.5 }}>
        {/* 客户案例 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: "primary.main", mb: 2 }}
              >
                ② 客户案例
              </Typography>
              {cases.length > 0 ? (
                cases.map((c, i) => (
                  <Box
                    key={i}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "brand.50",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "text.primary",
                        mb: 0.5,
                      }}
                    >
                      {c.customer || "—"}
                    </Typography>
                    {c.description && (
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}
                      >
                        {c.description}
                      </Typography>
                    )}
                    {c.feedback && (
                      <Typography sx={{ fontSize: 12, color: "success.main" }}>
                        {c.feedback}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                  待 skill 产出
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 常见问题 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: "primary.main", mb: 2 }}
              >
                常见问题
              </Typography>
              {faqs.length > 0 ? (
                faqs.map((f, i) => (
                  <Box
                    key={i}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "brand.50",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "text.primary",
                        mb: 0.5,
                      }}
                    >
                      Q: {f.question}
                    </Typography>
                    {f.answer && (
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        A: {f.answer}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                  待 skill 产出
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== ③ 客户问题与产品匹配（PRD §1.6 保留区块：每条以场景为核心） ===== */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography
            sx={{ fontSize: 14, fontWeight: 500, color: "primary.main", mb: 2 }}
          >
            ③ 客户问题与产品匹配
          </Typography>
          {matchList.length > 0 ? (
            matchList.map((m, i) => (
              <Box
                key={i}
                sx={{
                  mb: 1.5,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: "brand.50",
                  borderLeft: "3px solid", borderColor: "primary.main",
                }}
              >
                {/* 场景为标题 */}
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "primary.main",
                    mb: 1,
                  }}
                >
                  {m.scenario || "—"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                      客户描述
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: "text.primary", whiteSpace: "pre-wrap" }}
                    >
                      {m.customer_desc || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                      产品匹配
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: "primary.main", whiteSpace: "pre-wrap" }}
                    >
                      {m.product_match || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                      证据
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "pre-wrap" }}
                    >
                      {m.evidence || "—"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              待 skill 产出
            </Typography>
          )}
          <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 2 }}>
            · ③由 skill 从①提取客户描述+产品匹配，从②输入问题素材预生成 →
            确认后上传
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
